const idempotencyKeyPattern = /^[A-Za-z0-9._:-]{8,128}$/

export class BackendApiError extends Error {
    constructor(message, options = {}) {
        super(message)
        this.name = 'BackendApiError'
        this.status = options.status || 0
        this.code = options.code || 'backend_error'
        this.requestId = options.requestId || null
        if (options.cause) {
            this.cause = options.cause
        }
    }
}

export const createMinecardsBackendClient = ({
    baseUrl = '',
    getToken,
    fetchImpl = window.fetch.bind(window),
}) => {
    if (getToken !== undefined && typeof getToken !== 'function') {
        throw new TypeError('getToken must be a function when provided')
    }
    if (typeof fetchImpl !== 'function') {
        throw new TypeError('fetchImpl must be a function')
    }

    const normalizedBaseUrl = String(baseUrl).replace(/\/+$/, '')

    const request = async (path, options = {}) => {
        const headers = { Accept: 'application/json' }
        if (options.authenticated) {
            const token = await getToken?.()
            if (typeof token === 'string' && token) {
                headers.Authorization = `Bearer ${token}`
            }
        }
        if (options.idempotencyKey !== undefined) {
            if (!idempotencyKeyPattern.test(options.idempotencyKey)) {
                throw new BackendApiError('Invalid idempotency key', {
                    code: 'invalid_idempotency_key',
                })
            }
            headers['Idempotency-Key'] = options.idempotencyKey
        }

        let body
        if (options.body !== undefined) {
            headers['Content-Type'] = 'application/json'
            body = JSON.stringify(options.body)
        }

        let response
        try {
            response = await fetchImpl(`${normalizedBaseUrl}${path}`, {
                method: options.method || 'GET',
                headers,
                body,
                credentials: 'include',
            })
        } catch (error) {
            throw new BackendApiError('Backend request failed', {
                code: 'network_error',
                cause: error,
            })
        }

        const rawBody = await response.text()
        let payload = null
        if (rawBody) {
            try {
                payload = JSON.parse(rawBody)
            } catch (error) {
                throw new BackendApiError('Backend returned invalid JSON', {
                    status: response.status,
                    code: 'invalid_response',
                    cause: error,
                })
            }
        }

        if (!response.ok) {
            const apiError = payload?.error || {}
            throw new BackendApiError(
                apiError.message ||
                    `Backend request failed (${response.status})`,
                {
                    status: response.status,
                    code: apiError.code || 'http_error',
                    requestId: apiError.requestId,
                }
            )
        }
        return payload
    }

    const authenticated = (path, options = {}) =>
        request(path, { ...options, authenticated: true })

    const mutate = (method, path, idempotencyKey, body) => {
        if (!idempotencyKeyPattern.test(idempotencyKey || '')) {
            return Promise.reject(
                new BackendApiError('Invalid idempotency key', {
                    code: 'invalid_idempotency_key',
                })
            )
        }
        return authenticated(path, { method, idempotencyKey, body })
    }

    return {
        getBootstrap: () => authenticated('/api/v1/bootstrap'),
        getMe: () => authenticated('/api/v1/me'),
        getFriends: () => authenticated('/api/v1/friends'),
        getTopCreators: () => request('/api/v1/creators'),
        initializeProfile: () =>
            authenticated('/api/v1/me/initialize', { method: 'POST' }),
        getProfile: (handle) => {
            if (
                (typeof handle !== 'string' && typeof handle !== 'number') ||
                String(handle).trim() === ''
            ) {
                throw new BackendApiError('Profile handle is required', {
                    code: 'invalid_handle',
                })
            }
            return request(
                `/api/v1/profiles/${encodeURIComponent(String(handle))}`
            )
        },
        searchProfiles: (input) => {
            const normalizedInput = String(input).trim().replace(/^@/, '')
            if (!/^[A-Za-z0-9_]{1,30}$/.test(normalizedInput)) {
                throw new BackendApiError('Invalid profile search', {
                    code: 'invalid_query',
                })
            }
            return request(
                `/api/v1/profiles?query=${encodeURIComponent(normalizedInput)}`
            )
        },
        getCards: () => request('/api/v1/cards'),
        getCollections: () => request('/api/v1/collections'),
        getSpecial: () => request('/api/v1/special'),
        updateProfile: (patch, idempotencyKey) =>
            mutate('PATCH', '/api/v1/me', idempotencyKey, patch),
        activatePremium: (idempotencyKey) =>
            mutate('POST', '/api/v1/me/premium', idempotencyKey),
        setMoment: (cardId, status, idempotencyKey) =>
            mutate(
                'PUT',
                `/api/v1/me/moments/${positiveId(cardId)}`,
                idempotencyKey,
                { status }
            ),
        clearMoment: (cardId, idempotencyKey) =>
            mutate(
                'DELETE',
                `/api/v1/me/moments/${positiveId(cardId)}`,
                idempotencyKey
            ),
        addPin: (cardId, idempotencyKey) =>
            mutate(
                'PUT',
                `/api/v1/me/pins/${positiveId(cardId)}`,
                idempotencyKey
            ),
        removePin: (cardId, idempotencyKey) =>
            mutate(
                'DELETE',
                `/api/v1/me/pins/${positiveId(cardId)}`,
                idempotencyKey
            ),
        addFriend: (profileId, idempotencyKey) =>
            mutate(
                'PUT',
                `/api/v1/me/friends/${nonNegativeId(profileId)}`,
                idempotencyKey
            ),
        removeFriend: (profileId, idempotencyKey) =>
            mutate(
                'DELETE',
                `/api/v1/me/friends/${nonNegativeId(profileId)}`,
                idempotencyKey
            ),
        createSuggestion: (suggestion, idempotencyKey) =>
            mutate('POST', '/api/v1/suggestions', idempotencyKey, suggestion),
    }
}

const positiveId = (value) => {
    const number = Number(value)
    if (!Number.isSafeInteger(number) || number <= 0) {
        throw new BackendApiError('ID must be a positive integer', {
            code: 'invalid_id',
        })
    }
    return number
}

const nonNegativeId = (value) => {
    const number = Number(value)
    if (!Number.isSafeInteger(number) || number < 0) {
        throw new BackendApiError('ID must be a non-negative integer', {
            code: 'invalid_id',
        })
    }
    return number
}
